<template>
  <div class="overflow-x-hidden bg-neutral-50">
    <Head :title="$t('services.title')" />

    <div class="mx-auto w-full min-w-0 max-w-[1200px] px-4 py-8 sm:px-6 lg:px-0">
      <nav class="mb-6 flex min-w-0 flex-wrap items-center gap-1 text-sm text-neutral-700" aria-label="Breadcrumb">
        <Link :href="homeHref" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
          {{ $t('breadcrumbs.home') }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <template v-if="categoryFilter">
          <Link :href="servicesHref" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
            {{ $t('breadcrumbs.services') }}
          </Link>
          <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
          <span class="text-[#2ea8cf]">{{ categoryFilterLabel }}</span>
        </template>
        <span v-else class="text-[#2ea8cf]">{{ $t('breadcrumbs.services') }}</span>
      </nav>

      <h1 class="text-[32px] font-medium leading-10 tracking-[-0.02em] text-[#0c4a6e] lg:text-[40px] lg:leading-[48px]">
        {{ pageTitle }}
      </h1>

      <SpecialisationFilterList
        v-model="selectedSpecialisationId"
        :specialisations="availableSpecialisations"
        variant="page"
        section-class="mt-6"
        show-all-option
        scrollable-mobile
      />

      <IndexSearchBar
        class="mt-6"
        endpoint="/api/services/search"
        ids-key="service_ids"
        :placeholder="searchPlaceholder"
        :params="searchParams"
        v-model:matching-ids="matchingServiceIds"
      />

      <div v-if="filteredServices.length > 0" class="mt-8 lg:mt-10">
        <div class="grid min-w-0 grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          <Link
            v-for="service in filteredServices"
            :key="service.id"
            :href="serviceHref(service)"
            class="relative flex min-w-0 flex-col gap-2 overflow-hidden rounded-xl bg-white p-4 no-underline shadow-[0px_8px_24px_-3px_rgba(16,24,40,0.05),0px_8px_24px_-3px_rgba(16,24,40,0.1)] transition-shadow hover:shadow-md lg:p-6"
          >
            <div class="flex min-w-0 items-start justify-between gap-3">
              <span
                class="inline-flex w-fit shrink-0 items-center rounded-3xl bg-[#d5f6f0] px-3 py-1.5 text-sm font-medium leading-4 text-neutral-800"
              >
                {{ service.category_label }}
              </span>
              <span
                v-if="formatMeta(service)"
                class="shrink-0 text-sm font-semibold leading-4 text-green-600"
              >
                {{ formatMeta(service) }}
              </span>
            </div>

            <h2 class="min-w-0 break-words text-xl font-semibold leading-7 tracking-[-0.02em] text-neutral-900">
              {{ service.name }}
            </h2>

            <span class="inline-flex h-10 w-fit items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium leading-4 text-neutral-800">
              {{ $t('common.more') }}
              <svg class="size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </Link>
        </div>
      </div>

      <div v-else class="py-16 text-center">
        <h3 class="text-sm font-semibold text-neutral-900">
          {{ $t('services.empty_title') }}
        </h3>
        <p class="mt-1 text-sm text-neutral-500">
          {{ emptyHint }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import SpecialisationFilterList from '@/Shared/SpecialisationFilterList.vue';
import IndexSearchBar from '@/Shared/IndexSearchBar.vue';
import { formatServiceMeta } from '@/utils/serviceMeta';

export default {
  name: 'ServicesIndex',
  components: { Head, Link, SpecialisationFilterList, IndexSearchBar },
  props: {
    services: { type: Array, default: () => [] },
    specialisations: { type: Array, default: () => [] },
    // eslint-disable-next-line vue/prop-name-casing
    site_context: { type: String, default: null },
    // eslint-disable-next-line vue/prop-name-casing
    site_context_label: { type: String, default: '' },
  },
  data() {
    return {
      selectedSpecialisationId: null,
      categoryFilter: null,
      matchingServiceIds: null,
    };
  },
  computed: {
    homeHref() {
      const ctx = this.site_context;
      if (ctx && ctx !== 'code_medical_center' && this.$routes[ctx]) {
        return this.$routes[ctx]();
      }
      return '/';
    },
    categoryFilterLabel() {
      return this.$t(`service_categories.${this.categoryFilter}`);
    },
    pageTitle() {
      return this.categoryFilter ? this.categoryFilterLabel : this.$t('services.title');
    },
    servicesHref() {
      return this.$routes.services_index();
    },
    searchPlaceholder() {
      if (this.categoryFilter) {
        return this.$t(`services.search_placeholder.${this.categoryFilter}`);
      }
      return this.$t('services.search_placeholder.default');
    },
    searchParams() {
      return this.categoryFilter ? { category: this.categoryFilter } : {};
    },
    emptyHint() {
      if (this.matchingServiceIds) {
        return this.$t('services.empty_search_hint');
      }
      return this.$t('services.empty_hint');
    },
    servicesInCategory() {
      if (!this.categoryFilter) {
        return this.services;
      }
      return this.services.filter((service) => service.category === this.categoryFilter);
    },
    availableSpecialisations() {
      const ids = new Set();
      this.servicesInCategory.forEach((service) => {
        (service.specialisations || []).forEach((spec) => ids.add(spec.id));
      });
      return this.specialisations.filter((spec) => ids.has(spec.id));
    },
    filteredServices() {
      let list = this.servicesInCategory;

      if (this.selectedSpecialisationId) {
        list = list.filter((service) =>
          (service.specialisations || []).some((spec) => spec.id === this.selectedSpecialisationId),
        );
      }

      if (this.matchingServiceIds) {
        const idSet = new Set(this.matchingServiceIds);
        list = list.filter((service) => idSet.has(service.id));
      }

      return list;
    },
  },
  mounted() {
    if (typeof window !== 'undefined') {
      const category = new URLSearchParams(window.location.search).get('category');
      if (category && this.services.some((service) => service.category === category)) {
        this.categoryFilter = category;
      }
    }
  },
  methods: {
    serviceHref(service) {
      return this.$routes.service(service.slug);
    },
    formatMeta(service) {
      return formatServiceMeta(service);
    },
  },
};
</script>
